<template>
  <section class="k12-panel">
    <div class="k12-panel-header">
      <div class="k12-panel-title">
        <span class="k12-icon"><i class="fas fa-layer-group"></i></span>
        <span>{{ $t('preInvoiceV2.academicInfo') }}</span>
      </div>
      <a-tag v-if="targetReason" color="blue">{{ targetReason }}</a-tag>
    </div>
    <div class="k12-panel-body">
      <div class="program-row-grid">
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.mainProgram') }}</label>
          <a-input v-if="academicLocked" :value="mainProgramName" disabled />
          <a-select v-else v-model="model.acp_id" class="ts-w-full" show-search @change="emitIfUnlocked('main-program-change', $event)">
            <a-select-option v-for="item in mainPrograms" :key="item.acp_id" :value="item.acp_id">{{ item.acp_name_foreign }}</a-select-option>
          </a-select>
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.subProgram') }}</label>
          <a-input v-if="academicLocked" :value="subProgramName" disabled />
          <a-select v-else v-model="model.acps_id" class="ts-w-full" show-search @change="emitIfUnlocked('sub-program-change', $event)">
            <a-select-option v-for="item in subPrograms" :key="item.acps_id" :value="item.acps_id">{{ item.acps_name_foreign }}</a-select-option>
          </a-select>
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.academicYear') }}</label>
          <a-input v-if="academicLocked" :value="academicYearName" disabled />
          <a-select v-else v-model="model.ac_year_id" class="ts-w-full" show-search @change="emitIfUnlocked('academic-year-change', $event)">
            <a-select-option v-for="item in academicYears" :key="item.ac_year_id" :value="item.ac_year_id">{{ item.ac_year }}</a-select-option>
          </a-select>
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.academicTerm') }}</label>
          <a-select v-model="model.ac_term_id" class="ts-w-full" show-search option-label-prop="label" @change="$emit('academic-term-change')">
            <a-select-option
              v-for="item in academicTerms"
              :key="item.ac_term_id"
              :value="item.ac_term_id"
              :label="termOptionLabel(item)"
            >
              <span class="term-option">
                <span>{{ item.term_name }}</span>
                <a-tag v-if="termStatusName(item)" class="term-status-tag" :color="isCurrentTerm(item) ? 'green' : 'blue'">
                  {{ termStatusName(item) }}
                </a-tag>
              </span>
            </a-select-option>
          </a-select>
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.grade') }}</label>
          <a-select v-model="model.grade_id" class="ts-w-full" show-search @change="$emit('grade-change')">
            <a-select-option v-for="item in grades" :key="item.grade_id" :value="item.grade_id">{{ item.grade_name_foreign }}</a-select-option>
          </a-select>
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.studyTime') }}</label>
          <a-select
            v-model="model.study_time_id"
            class="ts-w-full"
            show-search
            allow-clear
            option-label-prop="label"
            :placeholder="$t('preInvoiceV2.studyTimePlaceholder')"
            :filter-option="filterOption"
            @change="$emit('study-time-change', $event)"
          >
            <a-select-option v-for="item in studyTimes" :key="item.ac_st_id" :value="item.ac_st_id" :label="item.study_time">
              {{ item.study_time }}
            </a-select-option>
          </a-select>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'AcademicInfoSection',
  props: {
    model: { type: Object, required: true },
    targetReason: { type: String, default: null },
    mainPrograms: { type: Array, required: true },
    subPrograms: { type: Array, required: true },
    academicYears: { type: Array, required: true },
    academicTerms: { type: Array, required: true },
    grades: { type: Array, required: true },
    studyTimes: { type: Array, required: true },
    currentAcademicTermId: { type: Number, default: null }
  },
  computed: {
    academicLocked() {
      return this.model.student_type === 'old'
    },
    mainProgramName() {
      const item = this.mainPrograms.find(row => row.acp_id === this.model.acp_id)
      return item ? item.acp_name_foreign : ''
    },
    subProgramName() {
      const item = this.subPrograms.find(row => row.acps_id === this.model.acps_id)
      return item ? item.acps_name_foreign : ''
    },
    academicYearName() {
      const item = this.academicYears.find(row => row.ac_year_id === this.model.ac_year_id)
      return item ? item.ac_year : ''
    }
  },
  methods: {
    emitIfUnlocked(eventName, payload) {
      if (this.academicLocked) return
      this.$emit(eventName, payload)
    },
    filterOption(input, option) {
      const label = option.componentOptions.propsData.label || ''
      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    isCurrentTerm(item) {
      if (Number(item.ac_status_id) === 1) return true
      return this.currentAcademicTermId && Number(item.ac_term_id) === Number(this.currentAcademicTermId)
    },
    termStatusName(item) {
      if (Number(item.ac_status_id) === 1) return this.$t('preInvoiceV2.currentAcademic')
      if (Number(item.ac_status_id) === 3) return this.$t('preInvoiceV2.nextAcademic')
      if (!this.currentAcademicTermId) return null
      return this.isCurrentTerm(item) ? this.$t('preInvoiceV2.currentAcademic') : this.$t('preInvoiceV2.nextAcademic')
    },
    termOptionLabel(item) {
      const status = this.termStatusName(item)
      return status ? `${item.term_name} - ${status}` : item.term_name
    }
  }
}
</script>

<style scoped>
.compact-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.program-row-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.term-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.term-status-tag {
  margin-right: 0;
  flex: 0 0 auto;
}

@media (max-width: 1200px) {
  .program-row-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .program-row-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .program-row-grid {
    grid-template-columns: 1fr;
  }
}
</style>
